/**
 * One build.
 *
 * The screenshots are the reason this tool exists, so they lead. The log is open while a build
 * runs and when one failed, and folded away when it passed -- at that point the artifacts are
 * the answer and twelve hundred lines of Gradle are not.
 */
import { useEffect, useRef, useState } from 'react';
import { ApiError, api, type Artifact, type Job } from '../api';
import { useJobStream } from '../useJobStream';
import { StepStrip } from './StepStrip';

const SHOT_CAPTIONS: Record<string, string> = {
  'screenshot:menu': 'Navigation drawer',
  'screenshot:about': 'About page',
  'screenshot:player': 'Player, after relaunch',
};

const FILE_WHAT: Record<string, string> = {
  aab: 'upload this to Play',
  apk: 'install directly to test',
  ipa: 'upload this to App Store Connect',
  'playstore-icon': '512 px store icon',
  report: 'machine-readable result',
  'build-log': 'full Gradle output',
  'smoke-log': 'logcat from the emulator',
};

function bytes(value: number | null): string {
  if (value === null) return '';
  if (value < 1024) return `${value} B`;
  if (value < 1024 * 1024) return `${Math.round(value / 1024)} KB`;
  return `${(value / 1048576).toFixed(1)} MB`;
}

function took(job: Job): string {
  if (!job.startedAt) return 'not started';
  const seconds = Math.round(((job.finishedAt ?? Date.now()) - job.startedAt) / 1000);
  if (seconds < 60) return `${seconds}s`;
  return `${Math.floor(seconds / 60)}m ${String(seconds % 60).padStart(2, '0')}s`;
}

export function JobView({ jobId, appName, canRemove, onToast, onRemoved }: {
  jobId: number;
  appName: string | null;
  canRemove: boolean;
  onToast: (message: string) => void;
  onRemoved: () => void;
}) {
  const { job, steps, active, lines, warnings } = useJobStream(jobId);
  const logRef = useRef<HTMLDivElement>(null);
  const pinned = useRef(true);
  const [showLog, setShowLog] = useState(false);
  const [confirmRemove, setConfirmRemove] = useState(false);

  const live = job ? job.state === 'running' || job.state === 'queued' : false;
  const failed = job?.state === 'failed';

  // Opened for the two cases where the log is the point, folded away when it is noise.
  useEffect(() => { setShowLog(live || failed); }, [live, failed]);

  useEffect(() => {
    const element = logRef.current;
    if (element && pinned.current) element.scrollTop = element.scrollHeight;
  }, [lines, showLog]);

  if (!job) return <div className="empty"><h1>Loading build {jobId}</h1></div>;

  const shots = job.artifacts.filter((a) => a.kind.startsWith('screenshot:') && !a.prunedAt);
  const files = job.artifacts.filter((a) => !a.kind.startsWith('screenshot:'));
  const downloadable = files.some((file) => !file.prunedAt);

  return (
    <div className="inner">
      <header className="run-head">
        <div className="run-title">
          <span className="run-number">#{job.id}</span>
          <h1>{appName ?? job.slug}</h1>
          <span className={`verdict ${job.state}`}>
            {job.state === 'passed' ? 'Passed' : job.state === 'failed' ? 'Failed'
              : job.state === 'running' ? 'Building' : job.state === 'queued' ? 'Queued' : 'Cancelled'}
          </span>
        </div>

        <dl className="facts">
          <div className="fact">
            <dt>Version</dt>
            <dd>{job.versionName ?? '—'}{job.versionCode ? ` (${job.versionCode})` : ''}</dd>
          </div>
          <div className="fact">
            <dt>Template</dt>
            <dd>{job.commit ? job.commit.slice(0, 9) : job.ref}</dd>
          </div>
          <div className="fact">
            <dt>Took</dt>
            <dd>{took(job)}</dd>
          </div>
          <div className="fact">
            <dt>Started by</dt>
            <dd>{job.requestedBy || '—'}</dd>
          </div>
        </dl>

        <div className="actions" style={{ marginTop: 20 }}>
          {downloadable && (
            <a className="btn" href={`/api/jobs/${job.id}/bundle.zip`}>Download everything</a>
          )}
          {live && (
            <button
              type="button"
              className="btn quiet"
              onClick={() => { void api.cancel(job.id).then(() => onToast(`Build #${job.id} cancelled`)); }}
            >
              Cancel build
            </button>
          )}
          {!live && !showLog && (
            <button type="button" className="btn quiet" onClick={() => setShowLog(true)}>
              Show log
            </button>
          )}
        </div>
      </header>

      <StepStrip steps={steps} active={active} />

      {job.failure && (
        <div className="failure">
          <h3>Stopped at {job.failure.step}</h3>
          <pre>{job.failure.message}</pre>
        </div>
      )}

      {warnings.length > 0 && (
        <div className="warnings">
          <h3>Worth a look</h3>
          {warnings.map((warning) => <p className="warning" key={warning}>{warning}</p>)}
        </div>
      )}

      {shots.length > 0 && (
        <section className="section">
          <div className="section-head">
            <h2>What the app looked like</h2>
            <span className="note">Captured on the emulator</span>
          </div>
          <div className="proof">
            {shots.map((shot) => (
              <figure key={shot.kind}>
                <img src={shot.url} alt={SHOT_CAPTIONS[shot.kind] ?? shot.name} loading="lazy" />
                <figcaption>{SHOT_CAPTIONS[shot.kind] ?? shot.name}</figcaption>
              </figure>
            ))}
          </div>
        </section>
      )}

      {files.length > 0 && (
        <section className="section">
          <div className="section-head">
            <h2>Files</h2>
            {downloadable && <a className="note" href={`/api/jobs/${job.id}/bundle.zip`}>All of them as one zip</a>}
          </div>
          <div className="files">
            {files.map((file) => <FileRow key={file.kind} file={file} />)}
          </div>
        </section>
      )}

      {showLog && (
        <section className="section">
          <div className="section-head">
            <h2>Log</h2>
            <span className="note log-note">
              <span>{live ? 'Streaming' : `${lines.length} lines`}</span>
              {!live && (
                <button type="button" className="plain" onClick={() => setShowLog(false)}>Hide</button>
              )}
            </span>
          </div>
          <div
            className="log"
            ref={logRef}
            onScroll={(event) => {
              const element = event.currentTarget;
              pinned.current = element.scrollHeight - element.scrollTop - element.clientHeight < 40;
            }}
          >
            {lines.length === 0
              ? <span className="log-empty">Nothing logged yet.</span>
              : lines.map((line) => (
                  <span className={`log-line ${line.tone}`} key={line.seq}>{line.text}</span>
                ))}
          </div>
        </section>
      )}

      {canRemove && !live && (
        <section className="danger-zone">
          <h2>Remove this build</h2>
          <p>
            Deletes the {files.length} file{files.length === 1 ? '' : 's'} above. The build stays in
            the history with its log, so you can still see that it happened and what it said.
          </p>
          {confirmRemove ? (
            <div className="actions">
              <button
                type="button"
                className="btn danger"
                onClick={() => {
                  api.deleteJob(job.id)
                    .then((result) => {
                      onToast(`Removed build #${job.id}, freed ${bytes(result.bytesFreed)}`);
                      onRemoved();
                    })
                    .catch((failure: ApiError) => onToast(failure.message));
                }}
              >
                Yes, delete the files
              </button>
              <button type="button" className="btn quiet" onClick={() => setConfirmRemove(false)}>Keep them</button>
            </div>
          ) : (
            <button type="button" className="btn quiet" onClick={() => setConfirmRemove(true)}>
              Remove files
            </button>
          )}
        </section>
      )}
    </div>
  );
}

function FileRow({ file }: { file: Artifact }) {
  if (file.prunedAt) {
    return (
      <div className="file gone">
        <span className="file-name">{file.name}</span>
        <span className="file-what">removed to free space — rebuild to get it back</span>
        <span className="file-size">{bytes(file.bytes)}</span>
      </div>
    );
  }
  return (
    <a className="file" href={file.url} download={file.name}>
      <span className="file-name">{file.name}</span>
      <span className="file-what">{FILE_WHAT[file.kind] ?? file.kind}</span>
      <span className="file-size">{bytes(file.bytes)}</span>
    </a>
  );
}
